// Presentational Buy-feed card for one imported catalog listing (plan T12).
//
// Pure + side-effect-free so it renders in an RTL test with no navigation,
// admin, or network mocking. Graceful degradation lives here: a NULL-hex colour
// renders a neutral swatch placeholder (never a crash), and an absent price
// shows "Price unavailable" rather than "$0".

import React, { useMemo } from 'react';
import { View, Text, StyleSheet, Pressable, Image } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../styles/ThemeProvider';
import MiniSparkline from '../MiniSparkline';
import type { CatalogListingCard, CatalogColorSwatch } from '../../lib/backendApi';
import { shouldShowBrandLine } from '../../lib/listingTitle';

const MAX_SWATCHES = 5;
// Below this absolute 30-day move we suppress the delta badge — sub-0.5% noise
// reads as "flat" and a flickering ±0.1% pill is more distracting than useful.
// Same threshold the old BuyScreen row used.
const MIN_DELTA_PCT = 0.5;

export function formatStartingAt(price: number | null | undefined): string {
  if (price == null) return 'Price unavailable';
  return `Starting at $${Math.round(price).toLocaleString()}`;
}

/**
 * The feed card (F2 `CatalogListingCard`) carries only the scalar 30-day delta,
 * not a full price series — the per-variant series lives on the listing-detail
 * response (`variant.price_history`). So the row sparkline is a faithful 2-point
 * directional line: where the representative variant sat 30 days ago vs now.
 * Returns [] (→ MiniSparkline renders nothing) when there's no delta to show.
 */
export function sparklineFromDelta(deltaPct: number | null | undefined): number[] {
  if (deltaPct == null) return [];
  return [1, 1 + deltaPct / 100];
}

interface Props {
  listing: CatalogListingCard;
  onPress?: () => void;
  // Catalog-debugging affordance (admin-only feed): when provided, the card shows a
  // category · family line and tapping the family filters the feed to that family so a
  // mis-grouped listing is one tap from "show me everything in this family". Omit to
  // hide the line entirely (e.g. in tests / a future end-user feed).
  onFilterFamily?: (family: string) => void;
}

export default function CatalogListingCardView({ listing, onPress, onFilterFamily }: Props) {
  const theme = useTheme();
  const initial = (listing.brand?.[0] ?? listing.listing_title[0] ?? '?').toUpperCase();
  const swatches = listing.color_swatches.slice(0, MAX_SWATCHES);
  const extraSwatches = listing.color_swatches.length - swatches.length;

  // F2 market fields. Old callers/fixtures may omit them — treat as absent.
  const delta = listing.price_delta_30d_pct ?? null;
  const showDelta = delta != null && Math.abs(delta) >= MIN_DELTA_PCT;
  const heldCount = listing.held_count ?? 0;
  const sparkData = sparklineFromDelta(delta);
  const trendColor = (delta ?? 0) >= 0 ? theme.colors.trendUp : theme.colors.trendDown;

  const styles = useMemo(
    () =>
      StyleSheet.create({
        card: {
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          padding: theme.spacing.md,
          marginHorizontal: theme.spacing.lg,
          marginBottom: theme.spacing.md,
        },
        thumb: {
          width: 64,
          height: 64,
          borderRadius: theme.radius.radiusSm,
          backgroundColor: theme.colors.backgroundElevated,
          justifyContent: 'center',
          alignItems: 'center',
          marginRight: theme.spacing.md,
          overflow: 'hidden',
        },
        thumbImage: { width: '100%', height: '100%' },
        thumbText: { ...theme.typography.h4, color: theme.colors.textTertiary },
        body: { flex: 1, minWidth: 0 },
        title: {
          fontFamily: theme.fontFamily.bodyMedium,
          fontSize: 15,
          lineHeight: 20,
          color: theme.colors.textPrimary,
        },
        brand: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
          marginTop: theme.spacing.xxs,
        },
        priceRow: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.xs,
          marginTop: theme.spacing.xs,
        },
        price: {
          ...theme.typography.xs,
          fontFamily: theme.fontFamily.mono,
          color: theme.colors.textSecondary,
        },
        // 30-day move pill — colour matches trendUp / trendDown so the same
        // semantics hold across the Cache + Buy surfaces (ported from BuyScreen).
        deltaBadge: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 2,
          paddingHorizontal: theme.spacing.xs,
          paddingVertical: 1,
          borderRadius: theme.radius.radiusSm,
        },
        deltaBadgeUp: { backgroundColor: theme.colors.trendUp + '22' },
        deltaBadgeDown: { backgroundColor: theme.colors.trendDown + '22' },
        deltaText: {
          ...theme.typography.xs,
          fontFamily: theme.fontFamily.bodyMedium,
          fontSize: 11,
        },
        deltaTextUp: { color: theme.colors.trendUp },
        deltaTextDown: { color: theme.colors.trendDown },
        held: {
          ...theme.typography.xs,
          fontFamily: theme.fontFamily.mono,
          color: theme.colors.textSecondary,
          marginTop: theme.spacing.xxs,
        },
        // Catalog-debug line: category · family identity. Muted + monospace so it reads
        // as metadata, not product copy. The family segment is tappable (filters the feed).
        debugRow: {
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          marginTop: theme.spacing.xxs,
        },
        debugText: {
          ...theme.typography.xs,
          fontFamily: theme.fontFamily.mono,
          fontSize: 11,
          color: theme.colors.textTertiary,
        },
        debugFamily: { color: theme.colors.accent },
        // Sparkline column — sits between the card body and the chevron. Fixed
        // width so rows with sparklines line up with rows without.
        sparkline: { marginRight: theme.spacing.sm },
        swatchRow: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.xs,
          marginTop: theme.spacing.sm,
        },
        swatch: {
          width: 14,
          height: 14,
          borderRadius: 7,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          backgroundColor: theme.colors.backgroundElevated,
        },
        moreSwatches: {
          ...theme.typography.xs,
          color: theme.colors.textTertiary,
          marginLeft: theme.spacing.xxs,
        },
      }),
    [theme],
  );

  return (
    <Pressable style={styles.card} onPress={onPress} accessibilityRole="button">
      <View style={styles.thumb}>
        {listing.hero_image_url ? (
          <Image
            source={{ uri: listing.hero_image_url }}
            style={styles.thumbImage}
            resizeMode="cover"
            accessibilityIgnoresInvertColors
          />
        ) : (
          <Text style={styles.thumbText}>{initial}</Text>
        )}
      </View>
      <View style={styles.body}>
        <Text style={styles.title} numberOfLines={2}>
          {listing.listing_title}
        </Text>
        {shouldShowBrandLine(listing.listing_title, listing.brand) && (
          <Text style={styles.brand}>{listing.brand}</Text>
        )}
        <View style={styles.priceRow}>
          <Text style={styles.price}>{formatStartingAt(listing.starting_at_price_usd)}</Text>
          {showDelta && (
            <View
              style={[styles.deltaBadge, delta! >= 0 ? styles.deltaBadgeUp : styles.deltaBadgeDown]}
            >
              <Ionicons
                name={delta! >= 0 ? 'arrow-up' : 'arrow-down'}
                size={9}
                color={trendColor}
              />
              <Text
                style={[styles.deltaText, delta! >= 0 ? styles.deltaTextUp : styles.deltaTextDown]}
              >
                {Math.abs(delta!).toFixed(1)}%
              </Text>
            </View>
          )}
        </View>
        {heldCount > 0 && <Text style={styles.held}>{heldCount} held</Text>}
        {onFilterFamily && (
          <View style={styles.debugRow}>
            <Text style={styles.debugText}>{listing.category ?? 'no-category'} · </Text>
            {listing.family ? (
              <Pressable
                onPress={() => onFilterFamily(listing.family!)}
                accessibilityRole="button"
                accessibilityLabel={`Filter feed by family ${listing.family}`}
                hitSlop={6}
              >
                <Text style={[styles.debugText, styles.debugFamily]}>{listing.family}</Text>
              </Pressable>
            ) : (
              <Text style={styles.debugText}>no-family</Text>
            )}
          </View>
        )}
        {swatches.length > 0 && (
          <View style={styles.swatchRow}>
            {swatches.map((swatch: CatalogColorSwatch, i: number) => (
              <View
                key={`${swatch.name}-${i}`}
                style={[
                  styles.swatch,
                  // hex is NULL until a hex backfill — fall back to a neutral
                  // placeholder fill rather than passing an invalid colour.
                  swatch.hex ? { backgroundColor: swatch.hex } : null,
                ]}
                accessibilityLabel={swatch.name}
              />
            ))}
            {extraSwatches > 0 && <Text style={styles.moreSwatches}>+{extraSwatches}</Text>}
          </View>
        )}
      </View>
      {showDelta && sparkData.length >= 2 && (
        // Gate the sparkline on the same MIN_DELTA_PCT floor as the pill so the two
        // signals agree — a sub-0.5% "flat" move shows neither a pill nor a sloped
        // line (drawing a trend line the pill calls flat reads as a glitch).
        <View style={styles.sparkline}>
          <MiniSparkline data={sparkData} color={trendColor} />
        </View>
      )}
      <Ionicons name="chevron-forward" size={18} color={theme.colors.textTertiary} />
    </Pressable>
  );
}
